import type { ReactNode } from 'react';
import { cx } from '../cx';
import { rem } from '../units';

export type WorkModel = 'remote' | 'hybrid' | 'onsite';
/** Work-model rings use the readout split colours, the same meaning everywhere. */
const WORK_RING: Record<WorkModel, string> = {
  remote: 'ring-jc-readout-remote',
  hybrid: 'ring-jc-readout-hybrid',
  onsite: 'ring-jc-readout-onsite',
};
const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jc-focus';

/**
 * The muted basemap colours from the active theme (land elevated, water iris-soft, roads border),
 * resolved to real colours for a map style. Read from `el` so a themed subtree is respected.
 */
export function basemapTone(el?: Element | null): { land: string; water: string; roads: string } {
  const target = el ?? (typeof document !== 'undefined' ? document.documentElement : null);
  const read = (name: string) => (target ? getComputedStyle(target).getPropertyValue(name).trim() : '');
  return { land: read('--jc-elevated'), water: read('--jc-forest-soft'), roads: read('--jc-border') };
}

export type MapPinProps = {
  /** Accessible name ("Registered nurse, Example Health Oy"). */
  label: string;
  selected?: boolean;
  /** The job's work model, drawn as the ring. */
  workModel?: WorkModel;
  onClick?: () => void;
  className?: string;
};

/**
 * A job pin as a DOM marker (Mapbox Marker element, or a static map): an ink dot with a surface ring;
 * selected adds a sun ring and lifts.
 */
export function MapPin({ label, selected = false, workModel, onClick, className }: MapPinProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        'block size-4 rounded-full bg-jc-ink ring-2 transition duration-(--jc-duration-fast) ease-jc-out motion-reduce:transition-none',
        // Focus is an outline so it never replaces the selected sun ring.
        FOCUS,
        selected
          ? '-translate-y-1 scale-125 shadow-jc-md ring-4 ring-jc-cta'
          : cx('shadow-jc-sm hover:scale-110', workModel ? cx('ring-4', WORK_RING[workModel]) : 'ring-jc-surface'),
        className,
      )}
    />
  );
}

/** Cluster diameter in px: grows with the square root of the count so area tracks the number. Pure. */
export function clusterSize(count: number): number {
  return Math.round(Math.min(72, Math.max(32, 22 + Math.sqrt(Math.max(0, count)) * 4)));
}

export type MapClusterProps = {
  count: number;
  /** The count formatted for the page language. */
  display: ReactNode;
  /** Accessible name ("128 jobs, zoom in"). */
  label: string;
  /** The dominant work model in the cluster, drawn as the ring. */
  workModel?: WorkModel;
  onClick?: () => void;
  className?: string;
};

/** A cluster of jobs: a surface disc with an ink ring and the count in the display face. */
export function MapCluster({ count, display, label, workModel, onClick, className }: MapClusterProps) {
  const size = clusterSize(count);
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{ width: rem(size), height: rem(size) }}
      className={cx(
        'inline-flex items-center justify-center rounded-full bg-jc-surface font-display text-sm font-bold tabular-nums text-jc-ink shadow-jc-sm ring-inset',
        workModel ? cx('ring-4', WORK_RING[workModel]) : 'ring-2 ring-jc-ink',
        'transition duration-(--jc-duration-fast) hover:scale-105 motion-reduce:transition-none',
        FOCUS,
        className,
      )}
    >
      <span aria-hidden="true">{display}</span>
    </button>
  );
}
